import { useState } from "react";
import { MAX_SHARE_EMAILS, SHARE_WORDS, SHARES } from "../../src/sharing/switches.ts";
import { api, type Share, type TripSharing, type TripView } from "./api.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { firstName } from "./faces.ts";
import { useGrow } from "./hooks.ts";
import { ReportThis } from "./ReportThis.tsx";
import { SettingsGroup } from "./SettingsGroup.tsx";
import { SwitchRow } from "./Switch.tsx";
import { Button, SegmentedControl } from "./souvenir.ts";
import { CopyLink } from "./ui.tsx";

// Share to view, in Trip settings (features/sharing.md, "Turning it on"): the owner lets people outside the trip look
// at it, read-only: anyone with its view link, or only people they list, seeing the plan and whatever the switches
// share. Only here, on the web, never through an AI. Everyone else in the trip sees whether it's shared, with whom
// and what, and chooses whether viewers see their own name, photo and ratings.

export function ShareToView({ trip, onChanged }: { trip: TripView; onChanged: () => void }) {
  const [sharing, setSharing] = useState<TripSharing | null>(trip.sharing ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!sharing) return null;
  const owner = trip.owner === trip.you;
  const act = async (run: () => Promise<TripSharing>) => {
    setBusy(true);
    setError(null);
    try {
      setSharing(await run());
      onChanged();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "That didn't work. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  return (
    <SettingsGroup id="share-title" title="Share to view">
      {owner ? (
        <OwnerControls trip={trip} sharing={sharing} busy={busy} act={act} />
      ) : (
        <MemberView trip={trip} sharing={sharing} busy={busy} act={act} />
      )}
      {error && (
        <div className="tm-settings-part">
          <p className="callout tm-error" role="alert">
            {error}
            <ReportThis />
          </p>
        </div>
      )}
    </SettingsGroup>
  );
}

type Act = (run: () => Promise<TripSharing>) => Promise<boolean>;

function OwnerControls({
  trip,
  sharing,
  busy,
  act,
}: {
  trip: TripView;
  sharing: TripSharing;
  busy: boolean;
  act: Act;
}) {
  const [asking, setAsking] = useState<"replace" | "stop" | null>(null);
  const [who, setWho] = useState<TripSharing["who"]>(sharing.who);
  const [emails, setEmails] = useState((sharing.emails ?? []).join("\n"));
  const growEmails = useGrow(emails);
  const typed = emails
    .split(/[\s,;]+/)
    .map((e) => e.trim())
    .filter(Boolean);
  const listChanged = typed.join("\n").toLowerCase() !== (sharing.emails ?? []).join("\n");
  if (!sharing.on)
    return (
      <div className="tm-settings-part">
        <p className="callout">Not shared outside the trip.</p>
        <p className="caption tm-muted">
          Let people outside the trip look at it, read-only, without joining: anyone with its link, or only people you
          list. You choose what they see; the rest stays in the trip.
        </p>
        <Button size="sm" className="tm-edge-start" disabled={busy} onClick={() => act(() => api.shareOn(trip.id))}>
          Share to view
        </Button>
      </div>
    );
  const link = sharing.link ?? "";
  const on = new Set(sharing.shares);
  const toggle = (share: Share, value: boolean) => {
    const next = SHARES.filter((s) => (s === share ? value : on.has(s)));
    return act(() => api.shareChange(trip.id, { shares: next }));
  };
  const others = trip.members.filter((m) => m.id !== trip.you);
  return (
    <>
      <div className="tm-settings-part">
        <span className="tm-settings-label">Who can view</span>
        <SegmentedControl
          label="Who can view"
          value={who}
          onChange={(value) => {
            const next = value as TripSharing["who"];
            setWho(next);
            if (next === "anyone" && sharing.who !== "anyone")
              void act(() => api.shareChange(trip.id, { who: "anyone" }));
            if (next === "list" && sharing.who !== "list" && typed.length > 0)
              void act(() => api.shareChange(trip.id, { who: "list", emails: typed }));
          }}
          options={[
            { value: "anyone", label: "Anyone with the link" },
            { value: "list", label: "Only people I list" },
          ]}
        />
        {who === "list" ? (
          <>
            <label className="tm-share-emails">
              <span className="caption tm-muted">
                {`Their emails, one a line, up to ${MAX_SHARE_EMAILS}. They sign in with Google to see it, with the email you list.`}
              </span>
              <textarea
                ref={growEmails}
                className="tm-share-emails-field body"
                rows={3}
                value={emails}
                spellCheck={false}
                autoCapitalize="off"
                placeholder={"jon@example.com\nana@example.com"}
                onChange={(e) => setEmails(e.target.value)}
              />
            </label>
            {(listChanged || sharing.who !== "list") && (
              <Button
                size="sm"
                className="tm-edge-start"
                disabled={busy || typed.length === 0}
                onClick={() => act(() => api.shareChange(trip.id, { who: "list", emails: typed }))}
              >
                {sharing.who === "list" ? "Save the list" : "Share with these people"}
              </Button>
            )}
            {sharing.who === "anyone" && (
              <p className="caption tm-muted">Until you share it with them, anyone with the link can still view it.</p>
            )}
            {sharing.who === "list" && sharing.listed === 0 && (
              <p className="caption tm-muted">Nobody is on the list: only the trip's own people can open the link.</p>
            )}
          </>
        ) : (
          <p className="caption tm-muted">Anyone you give the link to can look at it, signed in or not.</p>
        )}
      </div>
      <div className="tm-settings-part">
        <span className="tm-settings-label">The view link</span>
        <CopyLink url={link} />
        <div className="tm-share-actions">
          <a className="sv-btn sv-btn--secondary sv-btn--sm tm-button-link" href={link} target="_blank" rel="noopener">
            <span>Preview as a viewer</span>
          </a>
          <Button variant="quiet" size="sm" disabled={busy} onClick={() => setAsking("replace")}>
            Replace the link
          </Button>
          <Button variant="quiet" size="sm" disabled={busy} onClick={() => setAsking("stop")}>
            Stop sharing
          </Button>
        </div>
      </div>
      <div className="tm-settings-part">
        <fieldset className="tm-switch-list" aria-describedby="share-what-about">
          <legend className="tm-switch-list-legend">
            <span className="tm-settings-label">What they see</span>
          </legend>
          <p id="share-what-about" className="caption tm-muted tm-switch-list-about">
            Always the plan: its places, stays and journeys, the days and the map. Never where people are, questions,
            reminders, friends' pages or emails.
          </p>
          {SHARES.map((share) => (
            <SwitchRow
              key={share}
              label={SHARE_WORDS[share].label}
              about={SHARE_WORDS[share].covers}
              checked={on.has(share)}
              disabled={busy || (share === "booking_details" && !on.has("bookings"))}
              {...(share === "booking_details" && !on.has("bookings") ? { whenOff: "Only with Bookings on." } : {})}
              onChange={(value) => void toggle(share, value)}
            />
          ))}
        </fieldset>
      </div>
      {others.length > 0 && (on.has("names") || on.has("ratings")) && (
        <div className="tm-settings-part">
          <span className="tm-settings-label">Each person's own</span>
          <p className="caption tm-muted">
            Everyone in the trip chooses whether viewers see their name, photo and ratings. Until they do, they show as
            "A traveller".
          </p>
          <ul className="tm-share-people">
            {others.map((m) => (
              <li key={m.id} className="caption">
                <span className="body-strong">{firstName(m.name)}</span>
                <span className="tm-muted">{` · ${choiceWords(sharing.choices?.[m.id] ?? null)}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {asking === "replace" && (
        <ConfirmDialog
          title="Replace the link?"
          action="Replace the link"
          doing="Replacing…"
          busy={busy}
          error={null}
          onConfirm={async () => {
            if (await act(() => api.shareReplace(trip.id))) setAsking(null);
          }}
          onCancel={() => setAsking(null)}
        >
          <p className="body">The link you have now stops working at once. Anyone you gave it to needs the new one.</p>
        </ConfirmDialog>
      )}
      {asking === "stop" && (
        <ConfirmDialog
          title="Stop sharing?"
          action="Stop sharing"
          doing="Stopping…"
          busy={busy}
          error={null}
          onConfirm={async () => {
            if (await act(() => api.shareStop(trip.id))) setAsking(null);
          }}
          onCancel={() => setAsking(null)}
        >
          <p className="body">
            The link stops working at once, and the list of emails goes. Sharing it again makes a new link, with what's
            shared as you set it.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

// Everyone else in the trip: whether it's shared, with whom and what, and their own choice.
function MemberView({ trip, sharing, busy, act }: { trip: TripView; sharing: TripSharing; busy: boolean; act: Act }) {
  const ownerName = firstName(trip.members.find((m) => m.id === trip.owner)?.name ?? "") || "The owner";
  if (!sharing.on)
    return (
      <div className="tm-settings-part">
        <p className="callout">Not shared outside the trip.</p>
        <p className="caption tm-muted">{`Only ${ownerName} can share it to view, here.`}</p>
      </div>
    );
  const what = sharing.shares.map((s) => SHARE_WORDS[s].label);
  return (
    <>
      <div className="tm-settings-part">
        <p className="callout">
          {sharing.who === "list"
            ? `${ownerName} shares it to view with ${sharing.listed === 1 ? "one person" : `${sharing.listed} people`} outside the trip.`
            : `${ownerName} shares it to view with anyone who has its link.`}
        </p>
        <p className="caption tm-muted">
          {what.length > 0
            ? `They see the plan, read-only, and besides it: ${what.join(", ")}.`
            : "They see the plan, read-only, and nothing besides it."}
        </p>
      </div>
      {(sharing.shares.includes("names") || sharing.shares.includes("ratings")) && (
        <div className="tm-settings-part">
          <SwitchRow
            label="Show my name, photo and ratings"
            about={
              sharing.mine === null
                ? 'You haven\'t chosen yet: viewers see you as "A traveller" until you do.'
                : sharing.mine
                  ? "Viewers see your first name, your photo and your ratings."
                  : 'Viewers see you as "A traveller", in your colour, and none of your ratings.'
            }
            checked={sharing.mine === true}
            disabled={busy}
            onChange={(show) => void act(() => api.shareMine(trip.id, show))}
          />
        </div>
      )}
    </>
  );
}

const choiceWords = (shown: boolean | null) =>
  shown === true ? "shown" : shown === false ? "kept hidden" : "hasn't chosen yet";
